import { AssetDataGeoJsonLayer, AssetFilterInventory } from './asset.type'
import { Filter } from './filter.type'
import { FilterAppliedResult } from './plan.type'

export function getImpressionsForAssets(data: AssetDataGeoJsonLayer[]): number {
  let impressions = 0
  data.forEach((asset) => {
    if (!asset.properties.excluded) {
      impressions += asset.properties.impressions ?? 0
    }
  })
  return impressions
}

export function setExcludedPropertyToAssetsBySubFilter(
  allAssets: AssetDataGeoJsonLayer[],
  assets: AssetDataGeoJsonLayer[],
  excludedIds: string[],
): AssetDataGeoJsonLayer[] {
  const excludedAssets: AssetDataGeoJsonLayer[] = []
  const assetsWithoutExcluded = assets.filter((asset) => {
    const isExcludedAsset = excludedIds.includes(asset.properties.id)
    if (isExcludedAsset) {
      const assetExcluded = {
        ...asset,
        properties: { ...asset.properties, excluded: true },
      }
      excludedAssets.push(assetExcluded)
    }
    return !isExcludedAsset
  })
  const assetsCombined = [...assetsWithoutExcluded, ...excludedAssets]

  let excludedAssetsOutsideFilter: string[] = []
  if (excludedAssets.length > 0) {
    excludedAssetsOutsideFilter = excludedIds.filter((id) => {
      return !excludedAssets.find((asset) => asset.properties.id === id)
    })
  } else {
    excludedAssetsOutsideFilter = [...excludedIds]
  }

  const excludedAssetsOutsideFilterWithProperties: AssetDataGeoJsonLayer[] = []
  for (const id of excludedAssetsOutsideFilter) {
    const asset = allAssets.find((asset) => asset.properties.id === id)
    if (asset) {
      const excludedAsset = {
        ...asset,
        properties: {
          ...asset.properties,
          excluded: true,
          excludedLayer: true,
        },
      }
      excludedAssetsOutsideFilterWithProperties.push(excludedAsset)
    } else {
      console.debug(
        `🎃 Asset with id ${id} not found when trying to exclude it from the plan. Actual number of assets: ${allAssets.length}`,
      )
    }
  }
  const assetsCombinedWithExcludedOutsideFilter = [
    ...assetsCombined,
    ...excludedAssetsOutsideFilterWithProperties,
  ]

  return assetsCombinedWithExcludedOutsideFilter
}

/**
 *
 * @param assets Assets filtered at the moment
 * @param includedIds Assets included by the user that are not taken into account by filter. These assets are included via the Excluded Assets Layer
 * @returns Filtered assets, plus the ones included manually by the user via the map
 */
export function addIncludedAssetsFromExcludeLayer(
  allAssets: AssetDataGeoJsonLayer[],
  assets: AssetDataGeoJsonLayer[],
  includedIds: string[],
): FilterAppliedResult {
  if (!includedIds || includedIds.length === 0) {
    return { assets, filterApplied: false }
  }
  const includedAssets: AssetDataGeoJsonLayer[] = []
  includedIds.forEach((id) => {
    const asset = assets.find((asset) => asset.properties.id === id)
    if (!asset) {
      const asset = allAssets.find((asset) => asset.properties.id === id)
      if (asset) {
        includedAssets.push(asset)
      } else {
        console.debug(
          `🎃 Asset with id ${id} not found when trying to include it from the exclude layer. Actual number of assets: ${allAssets.length}`,
        )
      }
    }
  })

  const filteredAssets = [...assets, ...includedAssets]
  return { assets: filteredAssets, filterApplied: true }
}

function filterAssetsByInventoryOnMode(
  assets: AssetDataGeoJsonLayer[],
  filterParam: AssetFilterInventory,
  exclusionMode: boolean,
): AssetDataGeoJsonLayer[] {
  const filteredAssets = assets.filter((asset) => {
    const { properties } = asset
    let value: string | null = null
    if (filterParam.field === 'PlantUnitId') {
      value = properties.name?.toString() || null
    } else if (filterParam.field === 'GeopathId') {
      value = properties.external_panel_id?.toString() || null
    } else if (filterParam.field === 'id') {
      value = properties.id
    } else {
      value = properties.internal_panel_id?.toString() || null
    }

    if (!value) {
      return false
    }
    if (exclusionMode) {
      return !filterParam.ids.includes(value)
    }
    return filterParam.ids.includes(value)
  })
  return filteredAssets
}

function filterAssetsByInventoryIncluded(
  assets: AssetDataGeoJsonLayer[],
  subfilter: Filter,
): FilterAppliedResult {
  const filteredAssets = filterAssetsByInventoryOnMode(
    assets,
    subfilter.panels,
    false,
  )
  return { assets: filteredAssets, filterApplied: true }
}

export function filterAssetsByInventory(
  allAssets: AssetDataGeoJsonLayer[],
  assets: AssetDataGeoJsonLayer[],
  subFilter: Filter,
): FilterAppliedResult {
  let filterApplied = false

  const inventoryIncludedPartialResult: FilterAppliedResult =
    filterAssetsByInventoryIncluded(assets, subFilter)
  filterApplied = inventoryIncludedPartialResult.filterApplied || filterApplied

  return { assets: inventoryIncludedPartialResult.assets, filterApplied }
}

export function filterAssetsByInventoryOverwrittingAssets(
  allAssets: AssetDataGeoJsonLayer[],
  subFilter: Filter,
): FilterAppliedResult {
  let filterApplied = false

  const inventoryIncludedPartialResult: FilterAppliedResult =
    filterAssetsByInventoryIncluded(allAssets, subFilter)
  filterApplied = inventoryIncludedPartialResult.filterApplied || filterApplied

  return { assets: inventoryIncludedPartialResult.assets, filterApplied }
}
